import { useEffect } from "react";
import seoData from "@/lib/seo-pages.json";

/**
 * Lightweight, dependency-free per-page SEO.
 *
 * Sets the document <title>, meta description, canonical URL, and Open Graph /
 * Twitter Card tags for the current route. Because the site is a client-rendered
 * SPA, these are applied at runtime so each public page reports distinct metadata
 * to crawlers that execute JavaScript (Googlebot) and to link-unfurlers.
 *
 * The same source data (src/lib/seo-pages.json) is consumed at build time by
 * scripts/prerender.mjs, which bakes these tags into a static HTML file per
 * public route — so non-JS crawlers get correct metadata too. Keep both in sync
 * by editing only the JSON; never hard-code page metadata here or in the pages.
 */

export const SITE_NAME = seoData.siteName;
export const SITE_URL = seoData.siteUrl;
const DEFAULT_OG_IMAGE = seoData.ogImage;

export interface SeoOptions {
  /** Page title. " · UnfoldWin" is appended unless `titleAsIs` is true. */
  title: string;
  /** Meta description (~150–160 chars ideal). */
  description: string;
  /** Path (e.g. "/pricing") used to build the canonical + og:url. */
  path?: string;
  /** Absolute URL to a social preview image. Defaults to the shared OG image. */
  image?: string;
  /** Use `title` verbatim without appending the site name. */
  titleAsIs?: boolean;
  /** Set to true to keep the page out of search results (e.g. auth pages). */
  noIndex?: boolean;
}

/**
 * Typed map of path → SeoOptions, built from the JSON single source. Pages pass
 * `SEO_PAGES["/pricing"]` to `useSeo` so page metadata lives in exactly one place.
 */
export const SEO_PAGES: Record<string, SeoOptions> = Object.fromEntries(
  Object.entries(seoData.pages).map(([path, meta]) => [path, { ...meta, path }]),
);

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSeo({
  title,
  description,
  path,
  image = DEFAULT_OG_IMAGE,
  titleAsIs = false,
  noIndex = false,
}: SeoOptions) {
  useEffect(() => {
    const fullTitle = titleAsIs ? title : `${title} · ${SITE_NAME}`;
    const url = path ? `${SITE_URL}${path}` : undefined;

    document.title = fullTitle;
    setMeta("name", "description", description);
    setMeta("name", "robots", noIndex ? "noindex, nofollow" : "index, follow");

    // Open Graph
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:image", image);
    if (url) setMeta("property", "og:url", url);

    // Twitter
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);

    if (url) setLink("canonical", url);
  }, [title, description, path, image, titleAsIs, noIndex]);
}
